'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, Copy } from 'lucide-react';

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const defaultHours = [
  { day: 'Monday', closed: false, open: '07:30', close: '17:30' },
  { day: 'Tuesday', closed: false, open: '07:30', close: '17:30' },
  { day: 'Wednesday', closed: false, open: '07:30', close: '17:30' },
  { day: 'Thursday', closed: false, open: '07:30', close: '17:30' },
  { day: 'Friday', closed: false, open: '07:30', close: '16:30' },
  { day: 'Saturday', closed: false, open: '08:00', close: '12:00' },
  { day: 'Sunday', closed: true, open: '', close: '' },
];

export default function OpeningHoursTab() {
  const [hours, setHours] = useState(defaultHours);
  const [isLoading, setIsLoading] = useState(false);
  const [byAppointment, setByAppointment] = useState(false);

  const updateDay = (index: number, field: string, value: string | boolean) => {
    setHours((prev) => prev.map((h, i) => (i === index ? { ...h, [field]: value } : h)));
  };

  const copyMonToFri = () => {
    const mon = hours[0];
    setHours((prev) =>
      prev.map((h, i) => (i >= 1 && i <= 4 ? { ...h, open: mon.open, close: mon.close, closed: mon.closed } : h))
    );
    toast.success('Monday hours copied to Tuesday–Friday');
  };

  const handleSave = async () => {
    setIsLoading(true);
    // Backend integration point: PUT /api/businesses/{id}/opening-hours
    await new Promise((r) => setTimeout(r, 900));
    toast.success('Opening hours saved');
    setIsLoading(false);
  };

  return (
    <div>
      <div className="card-surface p-6 mb-5">
        <div className="flex items-center justify-between mb-5 pb-3 border-b border-border">
          <h3 className="font-bold text-base text-foreground">Weekly Opening Hours</h3>
          <button
            type="button"
            onClick={copyMonToFri}
            className="btn-secondary text-xs py-1.5 px-3"
          >
            <Copy size={12} />
            Copy Mon to Tue–Fri
          </button>
        </div>

        <div className="space-y-3">
          {hours.map((row, index) => (
            <div
              key={`hours-edit-${row.day}`}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-3 py-3 border-b border-border last:border-0"
            >
              <div className="w-28 shrink-0">
                <span className="text-sm font-semibold text-foreground">{row.day}</span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={row.closed}
                  onChange={(e) => updateDay(index, 'closed', e.target.checked)}
                  className="accent-negative w-4 h-4"
                />
                <span className="text-sm text-muted-foreground">Closed</span>
              </label>
              {!row.closed && (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="time"
                    value={row.open}
                    onChange={(e) => updateDay(index, 'open', e.target.value)}
                    className="input-field w-32 font-mono text-sm"
                    aria-label={`${row.day} opening time`}
                  />
                  <span className="text-muted-foreground text-sm">to</span>
                  <input
                    type="time"
                    value={row.close}
                    onChange={(e) => updateDay(index, 'close', e.target.value)}
                    className="input-field w-32 font-mono text-sm"
                    aria-label={`${row.day} closing time`}
                  />
                </div>
              )}
              {row.closed && (
                <span className="text-sm text-negative font-semibold">Closed all day</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="card-surface p-6 mb-6">
        <h3 className="font-bold text-base text-foreground mb-4">Additional Notes</h3>
        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={byAppointment}
              onChange={(e) => setByAppointment(e.target.checked)}
              className="accent-primary w-4 h-4"
            />
            <div>
              <span className="text-sm font-semibold text-foreground">Available by appointment outside hours</span>
              <p className="text-xs text-muted-foreground">Customers can request appointments outside your listed hours</p>
            </div>
          </label>
          <div>
            <label htmlFor="hours-notes" className="block text-sm font-semibold text-foreground mb-1.5">
              Bank holiday / seasonal notes
            </label>
            <textarea
              id="hours-notes"
              rows={3}
              className="input-field resize-none"
              placeholder="e.g. We close on all UK bank holidays. Summer hours June–August: Mon–Fri 7:00–18:00."
            />
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 -mx-6 flex justify-end gap-3">
        <button type="button" className="btn-secondary text-sm">Cancel</button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isLoading}
          className="btn-primary text-sm"
          style={{ minWidth: 160 }}
        >
          {isLoading ? <><Loader2 size={14} className="animate-spin" />Saving...</> : <><Save size={14} />Save Hours</>}
        </button>
      </div>
    </div>
  );
}